<template>
  <div class="light-r6" :class="{ disabled: isPlaying }">
    <CyberboardR6CdcPanel
      :baseData="profileNew.data"
      :defaultData="profileNew.default"
      :syncStatus="syncStatus || (isSyncing ? 'syncInProgress' : '')"
      @ready="onCdcReady"
      @state="onCdcState"
      @data="onCdcData"
      @error="onCdcError"
    />
    <CyberboardR6ConfigSelection
      v-model="showSyncSelection"
      purpose="sync"
      :currentPagePosition="lightPosition"
      @confirm="syncLightToDevice"
    />
    <CyberboardR6Navigation :lightPosition="lightPosition" />
    <div class="operation-hint" v-html="$t('light.title')" />
    <div class="play-panel">
      <i
        v-if="isPlaying"
        class="paly iconfont iconstop-o"
        @click="isPlaying = false"
      />
      <i v-else class="stop iconfont iconplay-o" @click="isPlaying = true" />
    </div>
    <div class="control-panel">
      <div class="left">
        <a-radio-group
          :value="lightPosition"
          size="large"
          @change="onLightIndexSelect"
          v-visible="!isPlaying"
        >
          <a-radio-button
            v-for="(pageIndex, position) in lightPageIndices"
            :key="pageIndex"
            :value="position"
          >
            {{ $t("light.name") }} {{ position + 1 }}
          </a-radio-button>
        </a-radio-group>
      </div>
      <div class="speed">
        <i class="iconfont iconlightning" />
        <span class="speed-label px">{{ $t("slow") }}</span>
        <a-slider
          v-model="speed"
          class="slider"
          :min="1"
          :max="16"
          :tip-formatter="(value) => value"
          :tooltipVisible="false"
        />
        <span class="speed-label px">{{ $t("fast") }}</span>
      </div>
      <div class="right" />
    </div>
    <div class="light-panel">
      <div class="remove">
        <div class="remove-hover">
          <i
            class="close iconfont iconclose"
            v-if="frameLength > 1 && !isPlaying"
            @click="removeFrame"
          />
          <i
            class="reset iconfont iconreset"
            v-if="frameLength > 1 && !isPlaying"
            @click="removeAllFrame"
          />
          <i
            class="iconfont iconminus-o"
            v-visible="frameLength > 1 && !isPlaying"
          />
        </div>
      </div>
      <c-led-strip
        class="lights"
        :isPlaying="isPlaying"
        :brightness="previewBrightness"
        :speed="speed"
        :frames="frames"
        :frameIndex="frameIndex"
        :selectedIndexes="selectedIndexes"
        @led-click="onLEDClick"
        @led-click-right="onLEDClickRight"
      />
      <div class="add" v-show="frameLength < 300 && !isPlaying">
        <i class="iconfont iconplus-o" @click="addFrame" />
        <span>{{ $t("light.add") }}</span>
      </div>
    </div>
    <div class="page-panel" v-visible="frameLength > 1 && !isPlaying">
      <i
        class="prev iconfont iconleft"
        v-visible="frameIndex > 0"
        @click="prevFrame"
      />
      <div class="page">01</div>
      <a-slider
        class="page-slider"
        :value="frameIndex + 1"
        :min="1"
        :max="frameLength"
        :tipFormatter="null"
        @change="changeFrame"
      />
      <div class="page-now px">{{ frameIndex + 1 }}</div>
      <div class="page">{{ frameLength }}</div>
      <i
        class="next iconfont iconright"
        v-visible="frameIndex < frameLength - 1"
        @click="nextFrame"
      />
    </div>
    <div class="action-panel" v-visible="!isPlaying">
      <c-led-converter @change="onConversionChange">
        <div class="upload px">
          <i class="iconfont icontupian" />
          {{ $t("light.imageConverter") }}
        </div>
      </c-led-converter>
      <div class="rotation px" @click="onRotate">
        <i class="iconfont iconxuanzhuan" />
        180° {{ $t("rotation") }}
      </div>
      <c-undo :key="'undo' + currentPageIndex" :data="frames" @undo="setFrames">
        <div class="undo px">
          <i class="iconfont iconchehui" /> {{ $t("undo") }}
        </div>
      </c-undo>
    </div>
    <div class="color-panel">
      <div class="slider-panel">
        <c-slider class="slider" :value="color" @input="onColorInput" />
        <c-color-picker v-model="color" :presetColor="presetColor" />
      </div>
      <div class="hex-color">
        HEX COLOR <span>#</span>
        <input type="text" :value="inputColor" @change="onColorInput" />
      </div>
    </div>
    <CyberboardR6SyncProgress
      v-if="isSyncing"
      :label="syncProgressLabel"
      :percent="syncProgressPercent"
    />
    <div class="buttons with-actions">
      <div class="button" @click="onBack">
        {{ $t("confirm") }} & {{ $t("back") }}
      </div>
      <button
        class="button sync-button"
        type="button"
        :disabled="isSyncing"
        @click="showSyncSelection = true"
      >
        {{ $t("cyberboardR6.sync") }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { deepCopy, speedList } from "@/utils";
import cyberboardR6SyncMixin from "./utils/cyberboardR6SyncMixin";
import CyberboardR6CdcPanel from "./CyberboardR6CdcPanel.vue";
import CyberboardR6SyncProgress from "./CyberboardR6SyncProgress.vue";
import CyberboardR6ConfigSelection from "./CyberboardR6ConfigSelection.vue";
import CyberboardR6Navigation from "./CyberboardR6Navigation.vue";
import {
  getR6Page,
  getR6LightPageIndices,
  buildR6SyncSelectionPlan,
  toLedStripFrames,
  toR6LedFrames,
} from "./r6Profile";

export default {
  name: "LightR6",
  components: {
    CyberboardR6CdcPanel,
    CyberboardR6SyncProgress,
    CyberboardR6ConfigSelection,
    CyberboardR6Navigation,
  },
  mixins: [cyberboardR6SyncMixin],
  data() {
    const position = Number(this.$route?.query?.light) - 1;
    return {
      lightPosition: [0, 1, 2].includes(position) ? position : 0,
      frames: [],
      frameIndex: 0,
      selectedIndexes: [],
      speed: 12,
      color: "#8028f5",
      presetColor: "#8028f5",
      isPlaying: false,
      initializedPage: false,
      showSyncSelection: false,
    };
  },
  computed: {
    ...mapState(["profileNew"]),
    lightPageIndices() {
      return getR6LightPageIndices();
    },
    currentPageIndex() {
      return this.lightPageIndices[this.lightPosition];
    },
    currentPage() {
      return getR6Page(this.profileNew.data, this.currentPageIndex);
    },
    previewBrightness() {
      return Number(this.currentPage?.lightness ?? 100) / 10;
    },
    frameLength() {
      return this.frames.length;
    },
    inputColor() {
      return this.color.replace("#", "");
    },
  },
  watch: {
    "profileNew.data.page_data": {
      deep: true,
      handler() {
        if (!this.initializedPage) this.initLight();
      },
    },
  },
  methods: {
    initLight() {
      this.frames = toLedStripFrames(this.currentPage);
      this.frameIndex = Math.min(
        this.frameIndex,
        Math.max(this.frames.length - 1, 0)
      );
      this.speed =
        speedList.findIndex((value) => value === this.currentPage?.speed_ms) +
          1 || 12;
      this.initializedPage = true;
    },
    onLightIndexSelect(event) {
      this.onSave();
      this.selectedIndexes = [];
      this.lightPosition = Number(event.target.value);
      this.initializedPage = false;
      this.$nextTick(this.initLight);
    },
    onLEDClick({ index }) {
      const frames = deepCopy(this.frames);
      if (!frames[this.frameIndex]) return;
      frames[this.frameIndex].frameRGB[index] = this.color;
      this.setFrames(frames);
      this.selectedIndexes = [];
      this.presetColor = this.color;
    },
    onLEDClickRight({ index, solid }) {
      const selected = [...this.selectedIndexes];
      const selectedIndex = selected.indexOf(index);
      if (selectedIndex >= 0) {
        if (!solid) selected.splice(selectedIndex, 1);
      } else {
        selected.push(index);
      }
      this.selectedIndexes = selected;
    },
    addFrame() {
      if (!this.frames.length) return;
      const frames = deepCopy(this.frames);
      frames.splice(this.frameIndex, 0, {
        frameRGB: [...frames[this.frameIndex].frameRGB],
      });
      this.setFrames(frames);
      this.frameIndex += 1;
    },
    removeFrame() {
      const frames = deepCopy(this.frames);
      frames.splice(this.frameIndex, 1);
      this.setFrames(frames);
      this.frameIndex = Math.min(
        this.frameIndex,
        Math.max(frames.length - 1, 0)
      );
    },
    removeAllFrame() {
      this.$confirm({
        title: this.$t("light.removeAllConfirm", [this.frameLength - 1]),
        onOk: () => {
          this.setFrames([deepCopy(this.frames[this.frameIndex])]);
          this.frameIndex = 0;
        },
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
      });
    },
    prevFrame() {
      this.frameIndex = Math.max(0, this.frameIndex - 1);
      this.selectedIndexes = [];
    },
    nextFrame() {
      this.frameIndex = Math.min(this.frameLength - 1, this.frameIndex + 1);
      this.selectedIndexes = [];
    },
    changeFrame(value) {
      this.frameIndex = value - 1;
      this.selectedIndexes = [];
    },
    onConversionChange({ RGB, frames } = {}) {
      if (Array.isArray(frames)) {
        const convertedFrames = frames.slice(0, 300).map((frame) => ({
          frameRGB: Array.isArray(frame?.frameRGB) ? [...frame.frameRGB] : [],
        }));
        if (
          !convertedFrames.length ||
          convertedFrames.some((frame) => frame.frameRGB.length !== 200)
        ) {
          this.$message.info(this.$t("light.fileErrorInfo"));
          return false;
        }
        this.setFrames(convertedFrames);
        this.frameIndex = 0;
      } else if (Array.isArray(RGB) && RGB.length === 200) {
        const convertedFrames = deepCopy(this.frames);
        convertedFrames.splice(
          this.frameIndex,
          convertedFrames[this.frameIndex] ? 1 : 0,
          { frameRGB: [...RGB] }
        );
        this.setFrames(convertedFrames);
      } else {
        this.$message.info(this.$t("light.fileErrorInfo"));
        return false;
      }
      this.selectedIndexes = [];
      this.onSave();
      return true;
    },
    onRotate() {
      this.setFrames(
        this.frames.map((frame) => ({
          ...frame,
          frameRGB: [...(frame.frameRGB || [])].reverse(),
        }))
      );
      this.selectedIndexes = [];
    },
    onKeydown(event) {
      const target = event.target;
      if (
        this.isPlaying ||
        target?.isContentEditable ||
        target?.closest?.(
          "input, textarea, select, button, [role='slider'], .c-color-picker"
        )
      ) {
        return;
      }
      if (
        event.ctrlKey &&
        (event.keyCode === 65 || event.key?.toLowerCase() === "a")
      ) {
        event.preventDefault();
        const count = this.frames[this.frameIndex]?.frameRGB?.length || 0;
        this.selectedIndexes =
          this.selectedIndexes.length === count
            ? []
            : Array.from({ length: count }, (_, index) => index);
        return;
      }

      const count = this.frames[this.frameIndex]?.frameRGB?.length || 0;
      if (!count) return;
      switch (event.keyCode) {
        case 37:
          event.preventDefault();
          this.moveLED(-5, count, (index) => index < 5);
          break;
        case 38:
          event.preventDefault();
          this.moveLED(-1, 5, (index) => index % 5 === 0);
          break;
        case 39:
          event.preventDefault();
          this.moveLED(5, -count, (index) => index >= count - 5);
          break;
        case 40:
          event.preventDefault();
          this.moveLED(1, -5, (index) => (index + 1) % 5 === 0);
          break;
        default:
          break;
      }
    },
    moveLED(step, offset, isEdge) {
      const frame = this.frames[this.frameIndex];
      const frameRGB = frame?.frameRGB;
      if (!Array.isArray(frameRGB) || !frameRGB.length) return;
      const hasSelection = this.selectedIndexes.length > 0;
      const sources = hasSelection
        ? [...this.selectedIndexes]
        : Array.from({ length: frameRGB.length }, (_, index) => index);
      const moves = sources
        .filter(
          (index) =>
            Number.isInteger(index) && index >= 0 && index < frameRGB.length
        )
        .map((index) => ({
          from: index,
          to: index + step + (isEdge(index) ? offset : 0),
        }))
        .filter(({ to }) => to >= 0 && to < frameRGB.length);
      const nextRGB = [...frameRGB];
      moves.forEach(({ from }) => {
        nextRGB[from] = "#000000";
      });
      moves.forEach(({ from, to }) => {
        nextRGB[to] = frameRGB[from];
      });
      const nextFrames = deepCopy(this.frames);
      nextFrames[this.frameIndex].frameRGB = nextRGB;
      this.setFrames(nextFrames);
      this.selectedIndexes = hasSelection ? moves.map(({ to }) => to) : [];
    },
    onColorInput(event) {
      const color = event.hex ? event.hex : `#${event.target.value}`;
      this.color = /^#[0-9A-F]{6}$/i.test(color) ? color : "#000000";
    },
    setFrames(frames) {
      this.frames = deepCopy(frames || []);
      this.frameIndex = Math.min(
        this.frameIndex,
        Math.max(this.frames.length - 1, 0)
      );
    },
    async syncLightToDevice(selection) {
      const plan = buildR6SyncSelectionPlan(
        selection,
        this.profileNew.data.page_data
      );
      return this.syncToDevice(
        plan.featureScope,
        undefined,
        plan.pageFeatureScope,
        async () => {
          const saveResult = this.onSave();
          if (!saveResult) return false;
          await saveResult;
          await this.$nextTick();

          const pages = this.lightPageIndices.map((pageIndex) => {
            const page = getR6Page(this.profileNew.data, pageIndex);
            return {
              pageIndex,
              frameCount: page?.frames?.frame_num || 0,
              speedMs: page?.speed_ms,
              sampleRgb: page?.frames?.frame_data?.[0]?.frame_RGB?.slice(0, 3),
            };
          });
          if (this.r6Bridge) {
            this.r6Bridge.log("light-sync:prepared", {
              featureScope: plan.featureScope,
              pageFeatureScope: plan.pageFeatureScope,
              selectedPageIndex: this.currentPageIndex,
              pages,
            });
          }
        }
      );
    },
    onSave() {
      const data = deepCopy(this.profileNew.data);
      const page = getR6Page(data, this.currentPageIndex);
      if (!page) return false;
      page.frames = {
        ...page.frames,
        valid: 1,
        frame_num: this.frames.length,
        frame_data: toR6LedFrames(this.frames),
      };
      page.speed_ms = speedList[this.speed - 1] || 90;
      return this.$store.dispatch("profileNew/setData", data);
    },
    onBack() {
      this.onSave();
      this.$router.push(this.$paths.indexR6);
    },
    goKeyboard() {
      this.onSave();
      this.$router.push(this.$paths.keyboardR6);
    },
  },
  mounted() {
    this.initLight();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    this.onSave();
    document.removeEventListener("keydown", this.onKeydown);
  },
};
</script>

<style lang="scss" scoped>
.light-r6 {
  min-height: inherit;
  padding: 40px 0 130px;
  color: #ffffff;

  &.disabled {
    user-select: none;
  }

  .operation-hint {
    margin-bottom: 30px;
    font-size: 20px;
    text-align: center;
  }

  .play-panel {
    @include flex-center;
    margin-bottom: 10px;

    .paly,
    .stop {
      @include icon-hover;
      font-size: 64px;
    }
  }

  .control-panel {
    @include flex-center;
    width: min(1443px, calc(100vw - 48px));
    margin: 0 auto 25px;

    .left,
    .right {
      width: 50%;
      flex: auto;
    }

    .speed {
      @include flex-center;
      flex: none;
      font-size: 12px;

      .speed-label.px {
        font-size: max(10px, 0.12rem);
      }

      .iconfont {
        font-size: 26px;
        line-height: 1;
        color: #ffffff;
        margin-right: 0.2rem;
      }

      .slider {
        width: 233px;
        margin: 0 30px 0 20px;
      }

      .iconfont {
        font-size: 22px;
      }
    }
  }

  .light-panel {
    @include flex-center;
    margin-bottom: 20px;
    position: relative;

    .remove,
    .add {
      flex: auto;
      @include flex-center;
      position: absolute;
      top: 40%;

      i {
        font-size: 36px;
        color: $primary-color;
        background: #171717;
        border-radius: 50%;
        overflow: hidden;
        position: relative;
        cursor: pointer;
      }

      span {
        display: none;
      }
    }

    .remove {
      justify-content: flex-end;
      right: 50%;
      margin-right: 750px;

      .remove-hover {
        position: relative;
        @include flex-center;
      }

      .close,
      .reset {
        width: 36px;
        height: 36px;
        @include flex-center;
        color: #ffffff;
        background: $primary-color;
        font-size: 22px;
        position: absolute;
      }

      .close {
        margin-top: -80px;
      }

      .reset {
        margin-top: 80px;
        font-size: 24px;
      }

      .iconminus-o {
        display: none;
      }
    }

    .lights {
      width: min(1443px, calc(100vw - 48px));
    }

    .add {
      justify-content: flex-start;
      left: 50%;
      margin-left: 750px;

      i {
        @include icon-hover;
        flex: none;
      }
    }
  }

  .page-panel {
    @include flex-center;
    position: relative;
    margin-bottom: 40px;

    .page-slider {
      width: 180px;
    }

    .prev,
    .next {
      @include icon-hover;
      font-size: 36px;
      padding: 5px 10px;
      margin: 0 50px;
    }

    .page {
      width: 60px;
      text-align: center;
    }

    .page-now {
      width: 50px;
      text-align: center;
      position: absolute;
      left: 50%;
      margin-left: -25px;
      bottom: -15px;
      font-size: 12px;
    }

    .page-now.px {
      font-size: max(10px, 0.12rem);
    }
  }

  .color-panel {
    @include flex-center;
    flex-direction: column;
    margin-bottom: 50px;

    .slider-panel {
      @include flex-center;
      margin-bottom: 30px;
      position: relative;

      .slider {
        width: 600px;
        margin: 0 30px;
      }

      .color {
        position: absolute;
        right: -20px;
        width: 46px;
        height: 46px;
        border-radius: 50%;
        border: 1px solid $primary-color;
        transform: translateX(50%);
        transition: all 0.1s;
        cursor: pointer;
      }
    }

    .hex-color {
      font-size: 20px;

      span {
        color: $primary-color;
        margin: 0 5px 0 30px;
      }

      input {
        width: 110px;
        text-align: center;
        color: $primary-color;
        user-select: text;
        border: 1px solid $primary-color;
      }
    }
  }

  .action-panel {
    @include flex-center;
    margin-bottom: 30px;

    .rotation,
    .upload,
    .undo {
      @include flex-center;
      width: auto;
      height: 40px;
      padding: 0 20px;
      margin-left: 30px;
      color: $primary-color;
      border: 1px solid $primary-color;
      border-radius: 10px;
      cursor: pointer;
      font-size: 14px;

      i {
        font-size: 26px;
        margin-right: 10px;
      }
    }

    .rotation.px,
    .upload.px,
    .undo.px {
      font-size: max(10px, 0.14rem);
    }

    .upload {
      margin-left: 0;

      i {
        font-size: 24px;
      }
    }
  }

  .buttons {
    @include flex-center;
    gap: 20px;

    &.with-actions .button {
      width: 180px;
      margin: 0;
    }

    .button {
      @include button;
      width: 200px;
      margin: 0 100px;
      cursor: pointer;

      &:disabled {
        cursor: wait;
        opacity: 0.65;
      }
    }
  }

  @media (max-width: 1500px) {
    .light-panel {
      .remove {
        right: auto;
        left: 0;
        margin-right: 0;
      }

      .add {
        right: 0;
        left: auto;
        margin-left: 0;
      }
    }
  }

  @media (max-width: 720px) {
    .operation-hint {
      margin: 0 16px 20px;
      font-size: 16px;
    }

    .control-panel {
      flex-direction: column;
      gap: 16px;

      .left,
      .right {
        width: auto;
      }

      .speed {
        justify-content: center;
        max-width: 100%;

        .slider {
          width: min(233px, 36vw);
          margin: 0 12px;
        }
      }
    }

    .light-panel {
      .remove {
        left: 0;
      }

      .add {
        right: 0;
      }
    }

    .page-panel {
      width: calc(100% - 24px);
      margin: 0 auto 28px;

      .page-slider {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
      }

      .prev,
      .next {
        margin: 0 6px;
      }
    }

    .action-panel {
      flex-wrap: wrap;
      gap: 8px;

      .upload,
      .rotation,
      .undo {
        padding: 0 8px;
        font-size: 12px;
        margin-left: 0;
      }

      .upload.px,
      .rotation.px,
      .undo.px {
        font-size: max(10px, 0.12rem);
      }
    }

    .buttons {
      flex-wrap: wrap;
    }
  }

  ::v-deep .page-slider {
    padding: 3px 0;

    .ant-slider-rail,
    .ant-slider-track {
      height: 4px;
    }

    .ant-slider-rail {
      background: #ffffff !important;
    }

    .ant-slider-handle {
      margin-top: -5px;
    }

    .ant-slider-handle:focus,
    .ant-slider-handle:hover {
      margin-top: -6px;
    }
  }
}
</style>
